/**
 * Admin/tenant UI–safe API failure copy.
 * Never surface developer / infra jargon (API request failed, HTTP 500, stacks, …).
 */
import { pickLocalizedApiMessage } from 'pi-kiosk-shared';
import i18n from '../i18n/i18n.js';

/** Capability-looking token: `foo:bar:baz` (at least two colons). */
const CAPABILITY_TOKEN_RE =
  /\b[a-z][a-z0-9_-]*:[a-z][a-z0-9_-]*(?::[a-z0-9_-]+)+\b/gi;

const DEVELOPER_MESSAGE_PATTERNS: readonly RegExp[] = [
  /\bAPI request failed\b/i,
  /\bRequest failed\b/i,
  /\bHTTP\s*\d{3}\b/i,
  /\bstatus\s*\d{3}\b/i,
  /\(\s*[45]\d{2}\s*\)/,
  /\bJSON\.?\s*parse\b/i,
  /\bUnexpected token\b/i,
  /\bInvalid JSON\b/i,
  /\bFailed to fetch\b/i,
  /\bNetwork error\b/i,
  /\bLoad failed\b/i,
  /\bTypeError\b/i,
  /\bSyntaxError\b/i,
  /\bECONNREFUSED\b/i,
  /\bENOTFOUND\b/i,
  /\bETIMEDOUT\b/i,
  /\bECONNRESET\b/i,
  /\bAbortError\b/i,
  /\bCORS\b/,
  /\bCSRF\b/i,
  /\bInternal Server Error\b/i,
  /\bBad Gateway\b/i,
  /\bService Unavailable\b/i,
  /\bGateway Timeout\b/i,
  /^\s*Unauthorized\s*$/i,
  /^\s*Forbidden\s*$/i,
  /^\s*Unauthenticated\s*$/i,
  /^\s*Access Denied\s*$/i,
  /^\s*Not Found\s*$/i,
  /^\s*Bad Request\s*$/i,
  /\bRate limited\b/i,
  /\bToo many requests\b/i,
  /\bat\s+\S+\s*\(/,
  /\bUNEXPECTED[_A-Z0-9]+\b/,
  /\bINVALID_JSON\b/i,
  /\bprisma\b/i,
  /\bOpenAPI\b/i,
  /\boutbox_events\b/i,
  /\bstack(trace)?\b/i,
  /^\s*Error:\s/i,
  /\bHTTP storm\b/i,
  /\bStorm guard\b/i,
  /\bfair queue overflow\b/i,
  /\bAPI response missing data\b/i,
  /\bSkipping cached failure\b/i,
  /\bSkipping denied request\b/i,
  // Backend ErrorIsolation / InfrastructureError public wraps
  /\bInfrastructure error\b/i,
  /\bOperation failed:\s*\w+UseCase\b/i,
  /\b\w+UseCase\.(execute|run)\b/,
  // Branding / metadata / deploy-status IT copy (must catch legacy metadataFailed strings)
  /\bbranding\s+metadata\b/i,
  /\bbranding\s+API\b/i,
  /\bmetadata\b/i,
  /\bAPI\s+is\s+available\b/i,
  /\bAPI\s+is\s+not\b/i,
  /\bAPI\s+not\s+deployed\b/i,
  /\bRetry\s+after\s+the\s+.+\s+API\b/i,
  /\bbackend\s+.+\s+routes\s+ship\b/i,
  // Owner/tenant lifecycle legalese — never show Mode A/B / closure-request jargon to operators
  /žádost o ukončení/i,
  /\bclosure request\b/i,
  /\bMode [AB]\b/,
  /\blegal closure\b/i,
  /právní uzavření/i,
  // Entitlement middleware / TER registry jargon (unlinked inferred, stay-unlinked, …)
  /\binferred route\b/i,
  /\bTenant Entitlement Route\b/i,
  /\bstay-unlinked\b/i,
  /\btenant entitlement\b/i,
  /\bRegister a Tenant Entitlement\b/i,
  // Platform / infra secret hygiene — never show env var or credential key names
  /\bPLATFORM_[A-Z0-9_]+\b/,
  /\bGOPAY_(?:GO_ID|CLIENT_ID|CLIENT_SECRET|API_URL)\b/,
  /\bAUTH_OIDC_[A-Z0-9_]*CLIENT_SECRET\b/,
  /\bVAPID_(?:PUBLIC|PRIVATE)_KEY\b/,
  /\bSet\s+[A-Z][A-Z0-9_]{2,}\b/,
  /\bCLIENT_SECRET\b/,
];

/** G3 — map backend lifecycle conflict codes to human delete/suspend copy. */
const OWNER_ORG_LIFECYCLE_CODE_MESSAGES: Readonly<Record<string, string>> = {
  CLOSURE_REQUEST_ALREADY_ACTIVE: 'shared.errors.orgDeleteAlreadyActive',
  CLOSURE_REQUEST_NOT_PENDING: 'shared.errors.orgDeleteCancelNotAllowed',
  CLOSURE_REQUEST_NOT_EXECUTABLE: 'shared.errors.orgDeleteNotExecutable',
  CLOSURE_REQUEST_NOT_EXECUTING: 'shared.errors.orgDeleteNotExecuting',
  CLOSURE_REQUEST_MODE_B_FORBIDDEN: 'shared.errors.orgDeletePhysicalForbidden',
  TENANT_LEGAL_CLOSURE_ACTIVE: 'shared.errors.orgLegalClosureActive',
};

/**
 * True when the message is mostly capability-looking codes (`foo:bar:baz`),
 * not operator-facing prose that happens to mention one entitlement.
 */
function looksLikeCapabilityCodeSoup(message: string): boolean {
  const matches = message.match(CAPABILITY_TOKEN_RE);
  if (matches === null || matches.length === 0) {
    return false;
  }
  const compact = message.replace(/[\s,;|/]+/g, '');
  if (compact.length === 0) {
    return false;
  }
  const tokenChars = matches.join('').length;
  // Whole message mostly codes (≥50% of non-separator chars are capability tokens)
  // or the message is only capability tokens / separators.
  if (tokenChars / compact.length >= 0.5) {
    return true;
  }
  return (
    /^[\s,;|/]*([a-z][a-z0-9_-]*:[a-z][a-z0-9_-]*(?::[a-z0-9_-]+)+[\s,;|/]*)+$/i.test(
      message,
    )
  );
}

const RAW_ENGLISH_WHEN_UI_NOT_EN: readonly RegExp[] = [
  /^\s*Unauthorized\s*$/i,
  /^\s*Forbidden\s*$/i,
  /^\s*Unauthenticated\s*$/i,
  /^\s*Access Denied\s*$/i,
  /^\s*Rate limited\s*$/i,
  /^\s*Too many requests\s*$/i,
  /^\s*Request failed\s*$/i,
  /^\s*Network error\s*$/i,
  /^\s*Not Found\s*$/i,
  /^\s*Bad Request\s*$/i,
  /^\s*Internal Server Error\s*$/i,
  /^\s*API request failed\b/i,
];

function tAdmin(key: string): string {
  return i18n.t(key, { ns: 'admin' });
}

function uiLocaleIsEnglish(): boolean {
  const lng = (i18n.resolvedLanguage ?? i18n.language ?? 'cs').toLowerCase();
  return lng.startsWith('en') || lng === 'pseudo';
}

/**
 * True when `message` looks like developer / infra jargon unsuitable for admin/tenant UI.
 */
export function looksLikeDeveloperErrorMessage(message: string): boolean {
  const trimmed = message.trim();
  if (trimmed.length === 0) {
    return true;
  }
  if (DEVELOPER_MESSAGE_PATTERNS.some((re) => re.test(trimmed))) {
    return true;
  }
  if (looksLikeCapabilityCodeSoup(trimmed)) {
    return true;
  }
  if (/^[A-Z][A-Z0-9_]{2,}$/.test(trimmed)) {
    return true;
  }
  if (
    /^[a-z][a-zA-Z0-9]*[A-Z][a-zA-Z0-9]*$/.test(trimmed) &&
    trimmed.length < 80
  ) {
    return true;
  }
  if (!uiLocaleIsEnglish() && RAW_ENGLISH_WHEN_UI_NOT_EN.some((re) => re.test(trimmed))) {
    return true;
  }
  if (
    trimmed.includes(' / ') &&
    /\b(Invalid|Unauthorized|Forbidden|HTTP|JSON|Error|Request failed|API request)\b/i.test(
      trimmed,
    )
  ) {
    return true;
  }
  return false;
}

/** True when the string is unsafe to show as admin/tenant UI copy. */
export function isUnsafeAdminApiMessage(message: string): boolean {
  return looksLikeDeveloperErrorMessage(message);
}

/** Status-based public fallback for admin/tenant operators. */
export function publicMessageForHttpStatus(status: number | undefined): string {
  if (status === 401) {
    return tAdmin('shared.errors.session');
  }
  if (status === 403) {
    return tAdmin('shared.errors.forbidden');
  }
  if (status === 404) {
    return tAdmin('shared.errors.notFound');
  }
  if (status === 409) {
    return tAdmin('shared.errors.conflict');
  }
  if (status === 422 || status === 400) {
    return tAdmin('shared.errors.validation');
  }
  if (status === 429) {
    return tAdmin('shared.errors.rateLimited');
  }
  if (status !== undefined && status >= 500) {
    return tAdmin('shared.errors.temporaryUnavailable');
  }
  if (status === 0) {
    return tAdmin('shared.errors.network');
  }
  return tAdmin('shared.errors.generic');
}

function isNetworkStyleMessage(message: string): boolean {
  const lower = message.toLowerCase();
  return (
    lower.includes('failed to fetch') ||
    lower.includes('network error') ||
    lower.includes('load failed') ||
    lower.includes('econnrefused') ||
    lower.includes('enotfound') ||
    lower.includes('etimedout') ||
    lower.includes('networkerror')
  );
}

/**
 * Rewrite legacy "žádost o ukončení" / closure-request passthrough into human delete copy.
 * Returns null when the message does not need a lifecycle rewrite.
 */
export function remapOwnerOrgLifecycleLegaleseMessage(message: string): string | null {
  const trimmed = message.trim();
  if (trimmed.length === 0) {
    return null;
  }
  const isClosureLegalese =
    /žádost o ukončení/i.test(trimmed) ||
    /\bclosure request\b/i.test(trimmed);
  if (!isClosureLegalese) {
    return null;
  }
  if (/zrušit|cancel/i.test(trimmed)) {
    return tAdmin('shared.errors.orgDeleteCancelFailed');
  }
  return tAdmin('shared.errors.orgDeleteFailed');
}

/**
 * Resolve admin/tenant-safe copy from status/code/optional raw text.
 * Prefer known codes and safe localized body text; never keep developer jargon.
 */
export function resolveAdminApiFailureMessage(input: {
  status: number;
  code?: string;
  rawMessage?: string;
}): string {
  const code = input.code;

  if (code === 'NETWORK_ERROR') {
    return tAdmin('shared.errors.network');
  }
  if (
    code === 'HTTP_STORM_DETECTED' ||
    code === 'HTTP_STORM_LATCHED' ||
    code === 'RATE_LIMITED' ||
    code === 'ADMIN_OUTBOUND_QUEUE_OVERFLOW'
  ) {
    return tAdmin('shared.errors.rateLimited');
  }
  if (
    code === 'INVALID_JSON' ||
    code === 'MISSING_DATA' ||
    code === 'INTERNAL' ||
    code === 'INTERNAL_ERROR'
  ) {
    return tAdmin('shared.errors.temporaryUnavailable');
  }
  if (code === 'UNAUTHORIZED' || code === 'UNAUTHENTICATED') {
    return tAdmin('shared.errors.session');
  }
  if (code === 'FORBIDDEN' || code === 'ACCESS_DENIED') {
    return tAdmin('shared.errors.forbidden');
  }
  if (
    code === 'TENANT_ENTITLEMENT_READ_DENIED' ||
    code === 'TENANT_ENTITLEMENT_WRITE_DENIED' ||
    code === 'TENANT_ENTITLEMENT_HARD_OFF'
  ) {
    return tAdmin('shared.errors.forbidden');
  }
  if (code === 'ADMIN_STEP_UP_REQUIRED') {
    return tAdmin('shared.errors.stepUpRequired');
  }
  if (code === 'API_DENY_CACHED') {
    return tAdmin('shared.errors.forbidden');
  }
  if (code != null) {
    const lifecycleMessageKey = OWNER_ORG_LIFECYCLE_CODE_MESSAGES[code];
    if (lifecycleMessageKey != null) {
      return tAdmin(lifecycleMessageKey);
    }
  }
  if (code === 'NOT_FOUND') {
    return tAdmin('shared.errors.notFound');
  }
  if (code === 'CONFLICT') {
    return tAdmin('shared.errors.conflict');
  }
  if (code === 'VALIDATION_ERROR' || code === 'BAD_REQUEST') {
    return tAdmin('shared.errors.validation');
  }

  const localized = pickLocalizedApiMessage(
    input.rawMessage ?? '',
    i18n.resolvedLanguage ?? i18n.language,
  ).trim();

  if (isNetworkStyleMessage(localized) || input.status === 0) {
    return tAdmin('shared.errors.network');
  }

  const lifecycleRewrite = remapOwnerOrgLifecycleLegaleseMessage(localized);
  if (lifecycleRewrite != null) {
    return lifecycleRewrite;
  }

  if (localized.length > 0 && isUnsafeAdminApiMessage(localized) === false) {
    return localized;
  }
  return publicMessageForHttpStatus(input.status);
}
